/* CPFS predictive search overlay — a focused, Spotify-style search surface.
   Progressive enhancement over a native /search form: the form submits normally
   without JS; with JS, typing streams grouped instant results (products + recipes)
   from Shopify's predictive-search JSON. No prices are shown — CPFS pricing is
   pending per the brief; the product subtitle is its category/brand instead. */
(() => {
  const MIN_QUERY = 2;
  const DEBOUNCE = 220;
  const LIMIT = 6;
  const RECENTS_KEY = 'cpfs:recent-searches';
  const RECENTS_MAX = 6;

  const esc = (s) =>
    String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // Ask the Shopify CDN for a small, sharp thumbnail.
  const thumb = (url, px) => {
    if (!url) return '';
    const u = typeof url === 'string' ? url : url.url || '';
    if (!u) return '';
    return u + (u.includes('?') ? '&' : '?') + 'width=' + px;
  };

  const getRecents = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
    } catch {
      return [];
    }
  };
  const pushRecent = (q) => {
    q = (q || '').trim();
    if (!q) return;
    try {
      const list = getRecents().filter((t) => t.toLowerCase() !== q.toLowerCase());
      list.unshift(q);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX)));
    } catch {
      /* storage disabled — recents are optional */
    }
  };
  const clearRecents = () => {
    try {
      localStorage.removeItem(RECENTS_KEY);
    } catch {
      /* noop */
    }
  };

  class CpfsSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('[data-search-input]');
      this.results = this.querySelector('[data-search-results]');
      this.form = this.querySelector('form');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.predictiveUrl = this.dataset.predictiveUrl || '/search/suggest';
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.controller = null;
      this.debounce = null;
    }

    connectedCallback() {
      this.openers = document.querySelectorAll('[data-search-open]');
      this.openers.forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        }),
      );
      this.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      // Backdrop click (outside the panel) closes.
      this.addEventListener('mousedown', (e) => {
        if (e.target === this) this.close();
      });
      this.input.addEventListener('input', () => this.onInput());
      this.clearBtn?.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      this.form?.addEventListener('submit', () => pushRecent(this.input.value));

      document.addEventListener('keydown', (e) => {
        const open = this.classList.contains('is-open');
        if (e.key === 'Escape' && open) {
          this.close();
          return;
        }
        // "/" quick-opens search when not already typing somewhere.
        if (e.key === '/' && !open && !/^(input|textarea|select)$/i.test(document.activeElement?.tagName || '')) {
          e.preventDefault();
          this.open();
        }
      });
      this.addEventListener('keydown', (e) => this.onArrowNav(e));
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cpfs-search-open');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      if (this.input.value.trim().length < MIN_QUERY) this.renderIdle();
      requestAnimationFrame(() => this.input.focus());
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cpfs-search-open');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }

    onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      clearTimeout(this.debounce);
      if (q.length < MIN_QUERY) {
        this.renderIdle();
        return;
      }
      this.debounce = setTimeout(() => this.fetchResults(q), DEBOUNCE);
    }

    async fetchResults(q) {
      this.controller?.abort();
      this.controller = new AbortController();
      const url =
        `${this.predictiveUrl}.json?q=${encodeURIComponent(q)}` +
        `&resources[type]=product,article&resources[limit]=${LIMIT}` +
        `&resources[options][unavailable_products]=last`;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
        const data = await res.json();
        this.renderResults(q, (data.resources && data.resources.results) || {});
      } catch (e) {
        if (e.name !== 'AbortError') {
          this.results.innerHTML = `<p class="v3-search__empty">Search is unavailable right now.</p>`;
        }
      } finally {
        this.results.removeAttribute('aria-busy');
      }
    }

    renderResults(q, r) {
      const products = r.products || [];
      const articles = r.articles || [];
      if (!products.length && !articles.length) {
        this.results.innerHTML = `<p class="v3-search__empty">No results for <strong>${esc(q)}</strong>.</p>`;
        return;
      }
      let html = '';
      if (products.length) html += this.group('Products', products.map((p) => this.productRow(p)).join(''));
      if (articles.length) html += this.group('Recipes', articles.map((a) => this.articleRow(a)).join(''));
      html +=
        `<a class="v3-search__all" href="${this.searchUrl}?q=${encodeURIComponent(q)}&options[prefix]=last">` +
        `See all results for &ldquo;${esc(q)}&rdquo;<span aria-hidden="true"> &rarr;</span></a>`;
      this.results.innerHTML = html;
    }

    group(label, rows) {
      return `<section class="v3-search__group"><h2 class="v3-search__group-head">${esc(label)}</h2>${rows}</section>`;
    }

    productRow(p) {
      const img = thumb(p.featured_image?.url || p.image, 96);
      const sub = p.product_type || p.vendor || 'Product';
      return this.row(p.url, img, p.title, sub);
    }

    articleRow(a) {
      const img = thumb(a.featured_image?.url || a.image, 96);
      return this.row(a.url, img, a.title, 'Recipe');
    }

    row(url, img, title, sub) {
      const media = img
        ? `<img class="v3-search__thumb" src="${esc(img)}" alt="" width="48" height="48" loading="lazy">`
        : `<span class="v3-search__thumb v3-search__thumb--ph" aria-hidden="true"></span>`;
      return (
        `<a class="v3-search__result" href="${esc(url)}">${media}` +
        `<span class="v3-search__result-text"><span class="v3-search__result-title">${esc(title)}</span>` +
        `<span class="v3-search__result-sub">${esc(sub)}</span></span></a>`
      );
    }

    renderIdle() {
      const recents = getRecents();
      let html = '';
      if (recents.length) {
        html +=
          `<section class="v3-search__group"><div class="v3-search__group-row">` +
          `<h2 class="v3-search__group-head">Recent searches</h2>` +
          `<button type="button" class="v3-search__clear-recents" data-clear-recents>Clear</button></div>` +
          `<div class="v3-search__chips">` +
          recents
            .map((t) => `<a class="v3-search__chip" href="${this.searchUrl}?q=${encodeURIComponent(t)}">${esc(t)}</a>`)
            .join('') +
          `</div></section>`;
      }
      html += `<p class="v3-search__hint">Search our products and recipes.</p>`;
      this.results.innerHTML = html;
      this.results.querySelector('[data-clear-recents]')?.addEventListener('click', () => {
        clearRecents();
        this.renderIdle();
      });
    }

    // Arrow-key navigation between the input and result links (Spotify-like).
    onArrowNav(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = Array.from(this.results.querySelectorAll('a'));
      if (!links.length) return;
      const active = document.activeElement;
      let idx = links.indexOf(active);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        idx = idx < 0 ? 0 : Math.min(idx + 1, links.length - 1);
        links[idx].focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx <= 0) {
          this.input.focus();
        } else {
          links[idx - 1].focus();
        }
      }
    }
  }

  if (!customElements.get('cpfs-search')) customElements.define('cpfs-search', CpfsSearch);
})();
